import { useState, useEffect, useCallback } from 'react';

const useStreamApi = (shareCode) => {
  const [metadata, setMetadata] = useState(null);
  const [streamSettings, setStreamSettings] = useState(null);
  const [isLoadingApi, setIsLoadingApi] = useState(false);
  const [apiError, setApiError] = useState(null);

  const fetchStreamData = useCallback(async () => {
    if (!shareCode) {
      // setApiError("Share code is not available to fetch stream data.");
      return;
    }

    setIsLoadingApi(true);
    setApiError(null);
    setMetadata(null);
    setStreamSettings(null);

    try {
      // First API call: Get metadata
      const metadataResponse = await fetch(`https://8b4uaj9s8k.execute-api.eu-west-2.amazonaws.com/Prod/share-code/code?code=${shareCode}`);
      if (!metadataResponse.ok) {
        const errorData = await metadataResponse.json().catch(() => ({ message: `Metadata request failed: ${metadataResponse.statusText}` }));
        throw new Error(errorData.message || 'Failed to fetch stream metadata.');
      }
      const metadataResult = await metadataResponse.json();
      if (!metadataResult.data) {
        throw new Error('Metadata not found in API response.');
      }
      setMetadata(metadataResult.data);

      if (!metadataResult.data.organisationID || !metadataResult.data.deviceName) {
        throw new Error('Organisation ID or Device Name is missing from metadata.');
      }

      // Second API call: Get token
      const tokenResponse = await fetch('https://8yf3gute1f.execute-api.eu-west-2.amazonaws.com/Prod/device-management/streamSettings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          organisationID: metadataResult.data.organisationID,
          deviceID: metadataResult.data.deviceName,
          uid: "0",
        }),
      });

      if (!tokenResponse.ok) {
        const errorData = await tokenResponse.json().catch(() => ({ message: `Token request failed: ${tokenResponse.statusText}` }));
        throw new Error(errorData.message || 'Failed to fetch stream token.');
      }
      const tokenResult = await tokenResponse.json();
      if (!tokenResult.streamSettings || !tokenResult.streamSettings.token) {
        throw new Error('Stream settings token not found in token API response.');
      }
      setStreamSettings({
        token: tokenResult.streamSettings.token,
        channel_name: metadataResult.data.deviceName, // Use deviceName as channel_name
        pinCode: metadataResult.data.pinCode || tokenResult.streamSettings.pinCode || null,
        camerControlEnabled: !!(metadataResult.data.camerControlEnabled),
        telemetryEnabled: !!(metadataResult.data.telemetryEnabled),
        mapEnabled: !!(metadataResult.data.mapEnabled),
        expiry: metadataResult.data.expiry || 0
      });

    } catch (err) {
      setApiError(err.message || 'An unexpected error occurred while fetching stream data.');
    } finally {
      setIsLoadingApi(false);
    }
  }, [shareCode]);

  useEffect(() => {
    if (shareCode) {
      fetchStreamData();
    }
  }, [shareCode]); // Only depend on shareCode, not fetchStreamData to prevent infinite loops

  return { metadata, streamSettings, isLoadingApi, apiError, retryFetchStreamData: fetchStreamData };
};

export default useStreamApi; 